// Admin picks while browsing THE VÖLT: tick a song (or a whole show) as you listen, then bring the picks in — straight on air if you like.
import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { toast } from "sonner";
import { Check, Plus, Download, Loader2, X, Radio } from "lucide-react";
import { API, useAuth } from "../../App";
import { logListen } from "./ListenLog";

const ax = { withCredentials: true };
let _picks = null;
const _subs = new Set();
const publish = (rows) => { _picks = rows; _subs.forEach((f) => f(rows)); };

export const useIsVoltAdmin = () => { const auth = useAuth(); const r = auth?.user?.role; return r === "admin" || r === "magician"; };

export const useVoltPicks = () => {
  const admin = useIsVoltAdmin();
  const [picks, set] = useState(_picks || []);
  useEffect(() => {
    if (!admin) return undefined;
    _subs.add(set);
    if (_picks == null) axios.get(`${API}/admin/volt/picks`, ax).then((r) => publish(r.data.picks)).catch(() => {});
    return () => _subs.delete(set);
  }, [admin]);
  const has = useCallback((show_id, file) => picks.some((p) => p.show_id === show_id && (!file || p.file === file)), [picks]);
  const add = useCallback(async (show_id, files, all = false, meta = null) => {
    try {
      const r = await axios.post(`${API}/admin/volt/picks`, { show_id, files, all }, ax); publish(r.data.picks); toast.success(all ? "Whole show picked for THE VÖLT" : "Picked for THE VÖLT");
      const mine = r.data.picks.filter((p) => p.show_id === show_id && (all || files.includes(p.file)));
      if (all && mine[0]) logListen({ kind: "picked", show_id, artist: mine[0].artist, album: mine[0].show_title, title: `whole show (${mine.length} tracks)`, date: mine[0].date, venue: mine[0].venue });
      else mine.forEach((p) => logListen({ kind: "picked", show_id, artist: p.artist, album: p.show_title, title: p.title, date: p.date, venue: p.venue }));
    }
    catch (e) { toast.error(e.response?.data?.detail || "Couldn't pick that"); }
  }, []);
  const remove = useCallback(async (show_id, file) => {
    try { const r = await axios.delete(`${API}/admin/volt/picks`, { ...ax, params: { show_id, file } }); publish(r.data.picks); }
    catch { toast.error("Couldn't remove"); }
  }, []);
  const refresh = useCallback(() => axios.get(`${API}/admin/volt/picks`, ax).then((r) => publish(r.data.picks)).catch(() => {}), []);
  return { admin, picks, has, add, remove, refresh, toggle: (show_id, file) => (has(show_id, file) ? remove(show_id, file) : add(show_id, [file])) };
};

// "+ show" on a show row: picks every track of the show (or un-picks the show).
export const PickShowButton = ({ show, dark }) => {
  const k = useVoltPicks();
  if (!k.admin) return null;
  const on = k.has(show.id);
  return (
    <button type="button" onClick={() => (on ? k.remove(show.id) : k.add(show.id, [], true))} className={`shrink-0 inline-flex items-center gap-0.5 rounded-full px-2 py-1 text-[10px] font-black transition-colors ${on ? "bg-[#54E3FF] text-[#120A24]" : dark ? "bg-white/10 hover:bg-white/20" : "bg-[#F3F1EC] hover:bg-[#E2E0DC]"}`} title={on ? "Un-pick this show" : "Pick the whole show for THE VÖLT"} aria-pressed={on} data-testid={`vault-pick-show-${show.id}`}>
      {on ? <Check className="h-3 w-3" /> : <Plus className="h-3 w-3" />} show
    </button>
  );
};

// Track list under the archive.org player: tick the song you're hearing.
export const ShowTrackPicker = ({ id, dark }) => {
  const k = useVoltPicks();
  const [show, setShow] = useState(null);
  useEffect(() => { if (!k.admin) return; setShow(null); axios.get(`${API}/admin/volt/shows/${id}`, ax).then((r) => setShow(r.data)).catch(() => setShow({ tracks: [] })); }, [id, k.admin]);
  if (!k.admin) return null;
  const all = show?.tracks?.length ? show.tracks.every((t) => k.has(id, t.file)) : false;
  return (
    <div className={`px-4 pb-3 ${dark ? "" : "text-[#1A1A1A]"}`} data-testid="vault-track-picker">
      <div className="flex items-center justify-between gap-2">
        <span className="text-[10px] font-black uppercase tracking-[0.18em] text-[#54E3FF]">Pick for THE VÖLT</span>
        {show?.tracks?.length > 0 && <button type="button" onClick={() => (all ? k.remove(id) : k.add(id, [], true))} className="text-[10px] font-bold underline" data-testid="vault-pick-all">{all ? "Un-pick all" : `Pick all ${show.tracks.length}`}</button>}
      </div>
      {!show ? <p className={`m-0 text-[11px] ${dark ? "text-white/60" : "text-[#5C5C5C]"}`}><Loader2 className="inline h-3 w-3 animate-spin mr-1" />Reading the track list…</p> : (
        <ul className="m-0 mt-1 p-0 list-none grid gap-x-4 sm:grid-cols-2 max-h-44 overflow-y-auto">
          {show.tracks.map((t, i) => {
            const on = k.has(id, t.file);
            return (
              <li key={t.file}>
                <label className={`flex items-center gap-2 py-0.5 text-xs cursor-pointer ${t.cached ? "opacity-70" : ""}`}>
                  <input type="checkbox" checked={on} onChange={() => k.toggle(id, t.file)} className="accent-[#54E3FF]" data-testid={`vault-pick-${i + 1}`} />
                  <span className={`w-5 text-right text-[10px] ${dark ? "text-white/40" : "text-[#9a9a9a]"}`}>{t.track_no}</span>
                  <span className="min-w-0 flex-1 truncate">{t.title}</span>
                  {t.cached && <span className={`rounded-full px-1.5 text-[9px] font-black ${t.cached.state === "on_air" ? "bg-[#54E3FF] text-[#120A24]" : dark ? "bg-white/15" : "bg-[#E2E0DC]"}`}>{t.cached.state === "on_air" ? "on air" : "in VÖLT"}</span>}
                </label>
              </li>
            );
          })}
        </ul>
      )}
    </div>
  );
};

// The tray: everything picked so far → "Bring into THE VÖLT" (optionally straight on air = into the Hoard player).
export const PicksTray = ({ dark }) => {
  const k = useVoltPicks();
  const [onAir, setOnAir] = useState(true);
  const [job, setJob] = useState(null);
  useEffect(() => {
    if (!job?.job_id || job.status === "done" || job.status === "error") return undefined;
    const t = setTimeout(() => axios.get(`${API}/admin/volt/jobs/${job.job_id}`, ax).then((r) => { setJob(r.data); if (r.data.status === "done") { toast.success(`${r.data.done} tape(s) in THE VÖLT${r.data.on_air ? " — on air now" : ""}`); k.refresh(); window.dispatchEvent(new CustomEvent("wall:music-changed")); } else if (r.data.status === "error") toast.error(r.data.error || "Import failed"); k.refresh(); }).catch(() => {}), 2500);
    return () => clearTimeout(t);
  }, [job]); // eslint-disable-line react-hooks/exhaustive-deps
  if (!k.admin || (!k.picks.length && !job)) return null;
  const go = async () => { try { const r = await axios.post(`${API}/admin/volt/picks/import`, { on_air: onAir }, ax); setJob({ job_id: r.data.job_id, status: "queued", total: r.data.count, done: 0, on_air: onAir }); } catch (e) { toast.error(e.response?.data?.detail || "Couldn't start"); } };
  const busy = job && job.status !== "done" && job.status !== "error";
  return (
    <div className={`mt-3 rounded-2xl p-3 ring-1 ${dark ? "bg-[#54E3FF]/10 ring-[#54E3FF]/40" : "bg-[#F0FBFD] ring-[#54E3FF]"}`} data-testid="volt-picks-tray">
      <div className="flex flex-wrap items-center gap-2">
        <span className="inline-flex items-center gap-1 text-[10px] font-black uppercase tracking-[0.18em] text-[#0891B2]"><Download className="h-3 w-3" /> {k.picks.length} picked for THE VÖLT</span>
        <label className="ml-auto inline-flex items-center gap-1 text-[11px] font-bold"><input type="checkbox" checked={onAir} onChange={(e) => setOnAir(e.target.checked)} className="accent-[#54E3FF]" data-testid="volt-picks-on-air" /><Radio className="h-3 w-3" /> straight on air (into the Hoard player)</label>
        <button type="button" onClick={go} disabled={busy || !k.picks.length} className="inline-flex items-center gap-1 rounded-full bg-[#54E3FF] text-[#120A24] px-3 py-1.5 text-xs font-black disabled:opacity-50" data-testid="volt-picks-import">
          {busy ? <><Loader2 className="h-3.5 w-3.5 animate-spin" /> {job.done || 0}/{job.total}{job.current ? ` · ${job.current}` : ""}</> : <>Bring into THE VÖLT</>}
        </button>
        {k.picks.length > 0 && !busy && <button type="button" onClick={() => k.remove()} className="text-[11px] opacity-60 hover:opacity-100" data-testid="volt-picks-clear">clear</button>}
      </div>
      {k.picks.length > 0 && (
        <ul className="m-0 mt-2 p-0 list-none flex flex-wrap gap-1" data-testid="volt-picks-list">
          {k.picks.map((p) => (
            <li key={`${p.show_id}/${p.file}`} className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] ${dark ? "bg-black/40" : "bg-white ring-1 ring-[#E2E0DC]"}`} data-testid="volt-pick-chip">
              <b className="truncate max-w-[160px]">{p.title}</b><span className="opacity-60 truncate max-w-[120px]">{p.artist}{p.date ? ` '${p.date.slice(2, 4)}` : ""}</span>
              <button type="button" onClick={() => k.remove(p.show_id, p.file)} aria-label="Remove pick" className="opacity-60 hover:opacity-100"><X className="h-3 w-3" /></button>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};
